import React, { useCallback, useMemo, useState, type JSX } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { AdminLocalizedLink } from "../../shared/locale/admin-dashboard-localized-link";
import { ADMIN_DEFAULT_TIME_RANGE_DAYS } from "../../contracts/admin-read-models";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminDetailPanel } from "../../shared/ui/admin-detail-panel";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminOperatorExplanationBlock } from "../../shared/ui/admin-operator-explanation-block";
import { AdminAiRuntimeSummaryStrip } from "../../shared/ui/admin-ai-runtime-summary-strip";
import { AdminAggregateSummaryStrip } from "../../shared/ui/admin-aggregate-summary-strip";
import { AdminSourceStatusGrid } from "../../shared/ui/admin-source-status-grid";
import type { AdminStateMetaItem } from "../../shared/ui/admin-empty-state";
import {
  buildAdminStateRouteMeta,
  buildAdminStateTimeRangeMeta,
} from "../../shared/ui/admin-screen-state-meta";
import { AdminStatusBadge, toAdminFreshnessBadgeValue } from "../../shared/ui/admin-status-badge";
import type { AdminStatusBadgeValue } from "../../shared/ui/admin-status-badge";
import { useOverviewScreenModel } from "./use-overview-screen-model";
import { useAdminManualRefreshRegistration } from "../../shared/query/admin-manual-refresh-context";

export type OverviewScreenProps = {
  dependencies: AdminDashboardDependencies;
};

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

function renderMetricCard(
  label: string,
  value: string,
  detail?: string,
): JSX.Element {
  return (
    <article key={label} style={cardStyle}>
      <p
        style={{
          margin: 0,
          color: "#64748b",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </p>
      <p
        style={{
          margin: "10px 0 0",
          fontSize: "1.6rem",
          fontWeight: 700,
          letterSpacing: "-0.03em",
        }}
      >
        {value}
      </p>
      {detail ? (
        <p
          style={{
            margin: "10px 0 0",
            color: "#475569",
            fontSize: "0.9rem",
            lineHeight: 1.5,
          }}
        >
          {detail}
        </p>
      ) : null}
    </article>
  );
}

function renderHotspotCard(
  label: string,
  value: string,
  detail: string,
  drilldownTo?: string,
): JSX.Element {
  const content = (
    <article
      style={{
        ...cardStyle,
        display: "grid",
        gap: "10px",
      }}
    >
      <p
        style={{
          margin: 0,
          color: "#64748b",
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </p>
      <p style={{ margin: 0, fontSize: "1.35rem", fontWeight: 700, letterSpacing: "-0.03em", color: "#18212b" }}>
        {value}
      </p>
      <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem", lineHeight: 1.6 }}>{detail}</p>
    </article>
  );

  if (!drilldownTo) {
    return content;
  }

  return (
    <AdminLocalizedLink to={drilldownTo} style={{ color: "inherit", textDecoration: "none" }}>
      {content}
    </AdminLocalizedLink>
  );
}

function buildOverviewStateMeta(locale: "ko" | "en"): AdminStateMetaItem[] {
  const now = new Date();
  const from = new Date(now.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const to = now.toISOString();

  return [
    buildAdminStateTimeRangeMeta(locale, from, to),
    buildAdminStateRouteMeta(locale, "/admin"),
  ];
}

function toFreshnessBadgeValue(value: "fresh" | "stale" | "partial" | "unavailable"): AdminStatusBadgeValue {
  return toAdminFreshnessBadgeValue(value);
}

export function buildOverviewSourceUnavailableMeta(
  viewModel: {
  generatedAtLabel: string;
  unavailableSources: string[];
  },
  locale: "ko" | "en",
): AdminStateMetaItem[] {
  return [
    {
      key: "affectedSources",
      label: pickAdminDashboardLocaleText(locale, { en: "Affected source", ko: "영향 소스" }),
      value: viewModel.unavailableSources.join(", "),
    },
    {
      key: "fallback",
      label: pickAdminDashboardLocaleText(locale, { en: "Fallback", ko: "대응" }),
      value: pickAdminDashboardLocaleText(locale, {
        en: "Keep unaffected panels visible and retry after the source recovers.",
        ko: "영향 없는 패널은 유지하고 소스 복구 후 다시 조회합니다.",
      }),
    },
    {
      key: "generatedAt",
      label: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
      value: viewModel.generatedAtLabel,
    },
  ];
}

export function OverviewScreen(props: OverviewScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [manualRefreshSignal, setManualRefreshSignal] = useState(0);
  const screenModelDependencies = useMemo(
    () => ({
      ...props.dependencies,
      locale,
    }),
    [props.dependencies, locale],
  );
  const state = useOverviewScreenModel(screenModelDependencies, manualRefreshSignal);
  const refreshCurrentScreen = useCallback(() => {
    setManualRefreshSignal((current) => current + 1);
  }, []);
  const manualRefreshRegistration = useMemo(
    () => ({ onRefresh: refreshCurrentScreen, refreshing: state.status === "loading" }),
    [refreshCurrentScreen, state.status],
  );
  useAdminManualRefreshRegistration(manualRefreshRegistration);
  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Overview", ko: "개요" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading operations cockpit...", ko: "운영 상황판을 불러오는 중입니다..." }),
    deniedTitle: pickAdminDashboardLocaleText(locale, { en: "Overview denied", ko: "개요 조회 권한 없음" }),
    deniedBody: pickAdminDashboardLocaleText(locale, { en: "Overview read permission is missing.", ko: "개요 조회 권한이 없습니다." }),
    failedTitle: pickAdminDashboardLocaleText(locale, { en: "Overview failed", ko: "개요 조회 실패" }),
    emptyTitle: pickAdminDashboardLocaleText(locale, { en: "No overview data", ko: "개요 데이터 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, {
      en: "No summary is available in the current default range (last 30 days). Drill directly into Users, Device KPIs, or Failed Flows.",
      ko: "현재 기본 범위(최근 30일)에 조회할 요약이 없습니다. 사용자, 실기기 지표, 오류 흐름 축으로 직접 내려가 확인하세요.",
    }),
    title: pickAdminDashboardLocaleText(locale, {
      en: "Operations cockpit for today health and primary drill-downs",
      ko: "오늘 상태와 주요 드릴다운을 위한 운영 상황판",
    }),
    description: pickAdminDashboardLocaleText(locale, {
      en: "Operators should be able to pick the closest axis among user data, real-device KPIs, and failed traces, while checking release hotspots and job backlog from the same screen.",
      ko: "사용자 데이터, 실기기 지표, 실패 추적 중 지금 가장 가까운 축을 바로 고르고, 같은 화면에서 릴리스 핫스팟과 작업 대기 현황을 확인할 수 있어야 합니다.",
    }),
    range: pickAdminDashboardLocaleText(locale, { en: "Range", ko: "범위" }),
    generated: pickAdminDashboardLocaleText(locale, { en: "Generated", ko: "생성 시각" }),
    request: pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" }),
    triageHint: pickAdminDashboardLocaleText(locale, { en: "Triage hint", ko: "조사 힌트" }),
    currentHotspot: pickAdminDashboardLocaleText(locale, { en: "Current hotspot", ko: "현재 핫스팟" }),
    openHotspot: pickAdminDashboardLocaleText(locale, { en: "Open hotspot", ko: "핫스팟 열기" }),
    sourceUnavailableTitle: pickAdminDashboardLocaleText(locale, { en: "Source unavailable", ko: "소스 사용 불가" }),
    sourceUnavailableBody: pickAdminDashboardLocaleText(locale, { en: "Some projection sources are unavailable right now.", ko: "일부 프로젝션 소스를 지금 읽을 수 없습니다." }),
    primaryAxes: pickAdminDashboardLocaleText(locale, { en: "Primary Axes", ko: "주요 축" }),
    primaryAxesTitle: pickAdminDashboardLocaleText(locale, {
      en: "Start with the workflow that matches the operator question",
      ko: "운영자 질문에 맞는 흐름부터 시작합니다",
    }),
    todayHealth: pickAdminDashboardLocaleText(locale, { en: "Today Health", ko: "오늘 상태" }),
    todayHealthTitle: pickAdminDashboardLocaleText(locale, {
      en: "Snapshot before you drill into detail",
      ko: "상세 조사 전에 보는 스냅샷",
    }),
    diagnosticEvidence: pickAdminDashboardLocaleText(locale, { en: "Diagnostic evidence", ko: "진단 근거" }),
    diagnosticEvidenceTitle: pickAdminDashboardLocaleText(locale, {
      en: "Read error concentration and weak use cases before branching out",
      ko: "오류 집중도와 약한 유스케이스를 먼저 읽고 가지를 칩니다",
    }),
    diagnosticEvidenceBody: pickAdminDashboardLocaleText(locale, {
      en: "These tables tell you whether the next move should be a failed-flow drill-down or a broader release comparison.",
      ko: "이 표들은 다음 이동이 오류 흐름 드릴다운인지, 더 넓은 릴리스 비교인지 먼저 판단하게 해 줍니다.",
    }),
    requests: pickAdminDashboardLocaleText(locale, { en: "Requests", ko: "요청 수" }),
    requestsBody: pickAdminDashboardLocaleText(locale, { en: "Total requests within the current range.", ko: "현재 범위의 전체 요청 수" }),
    server5xxBody: pickAdminDashboardLocaleText(locale, { en: "Server-side errors that require immediate failed-trace inspection.", ko: "즉시 실패 추적을 확인해야 하는 서버 측 오류" }),
    client4xxBody: pickAdminDashboardLocaleText(locale, { en: "Client-side errors from validation, auth, or contract boundaries.", ko: "입력값, 권한, 계약 경계에서 발생한 클라이언트 측 오류" }),
    topError: pickAdminDashboardLocaleText(locale, { en: "Top Error", ko: "상위 오류" }),
    count: pickAdminDashboardLocaleText(locale, { en: "Count", ko: "개수" }),
    feature: pickAdminDashboardLocaleText(locale, { en: "Feature", ko: "기능" }),
    useCase: pickAdminDashboardLocaleText(locale, { en: "Use Case", ko: "유스케이스" }),
    noTopErrors: pickAdminDashboardLocaleText(locale, { en: "No top errors", ko: "상위 오류 없음" }),
    noTopErrorsBody: pickAdminDashboardLocaleText(locale, { en: "There are no top error codes right now.", ko: "현재 상위 오류 코드가 없습니다." }),
    weakUseCases: pickAdminDashboardLocaleText(locale, { en: "Weak use cases", ko: "약한 유스케이스" }),
    failure: pickAdminDashboardLocaleText(locale, { en: "Failure", ko: "실패" }),
    rate: pickAdminDashboardLocaleText(locale, { en: "Rate", ko: "비율" }),
    noUseCaseHealth: pickAdminDashboardLocaleText(locale, { en: "No use case health", ko: "유스케이스 상태 없음" }),
    noUseCaseHealthBody: pickAdminDashboardLocaleText(locale, { en: "There are no use case health rows right now.", ko: "현재 유스케이스 상태 행이 없습니다." }),
    freshnessAndContext: pickAdminDashboardLocaleText(locale, { en: "Freshness and operator context", ko: "신선도와 운영자 문맥" }),
    overviewMeta: pickAdminDashboardLocaleText(locale, { en: "Overview meta", ko: "개요 메타" }),
    freshness: pickAdminDashboardLocaleText(locale, { en: "Freshness", ko: "신선도" }),
    sourceStatus: pickAdminDashboardLocaleText(locale, { en: "Source status", ko: "소스 상태" }),
    source: pickAdminDashboardLocaleText(locale, { en: "Source", ko: "소스" }),
    operatorChecklist: pickAdminDashboardLocaleText(locale, { en: "Operator checklist", ko: "운영자 체크리스트" }),
    start: pickAdminDashboardLocaleText(locale, { en: "Start", ko: "시작" }),
    delayed: pickAdminDashboardLocaleText(locale, { en: "Delayed", ko: "지연" }),
    unavailable: pickAdminDashboardLocaleText(locale, { en: "Unavailable", ko: "사용 불가" }),
    startBody: pickAdminDashboardLocaleText(locale, {
      en: "Open the axis closest to the current question first: user, KPI, or failed trace.",
      ko: "사용자, 실기기 지표, 실패 추적 중 현재 문의에 가까운 축을 먼저 엽니다.",
    }),
    none: pickAdminDashboardLocaleText(locale, { en: "none", ko: "없음" }),
    releaseAndJobs: pickAdminDashboardLocaleText(locale, { en: "Release And Jobs", ko: "릴리스와 작업" }),
    releaseAndJobsTitle: pickAdminDashboardLocaleText(locale, {
      en: "Hotspots that should immediately branch into supporting drill-downs",
      ko: "즉시 보조 드릴다운으로 이어져야 하는 핫스팟",
    }),
    jobHotspot: pickAdminDashboardLocaleText(locale, { en: "Job hotspot", ko: "작업 핫스팟" }),
    queued: pickAdminDashboardLocaleText(locale, { en: "Queued", ko: "대기" }),
    failed: pickAdminDashboardLocaleText(locale, { en: "Failed", ko: "실패" }),
    succeeded: pickAdminDashboardLocaleText(locale, { en: "Succeeded", ko: "성공" }),
    noJobHealth: pickAdminDashboardLocaleText(locale, { en: "No job health", ko: "작업 상태 없음" }),
    noJobHealthBody: pickAdminDashboardLocaleText(locale, { en: "There is no job health data right now.", ko: "현재 작업 상태 데이터가 없습니다." }),
    releaseHotspot: pickAdminDashboardLocaleText(locale, { en: "Release hotspot", ko: "릴리스 핫스팟" }),
    failures: pickAdminDashboardLocaleText(locale, { en: "Failures", ko: "실패 수" }),
    noReleaseHealth: pickAdminDashboardLocaleText(locale, { en: "No release health", ko: "릴리스 상태 없음" }),
    noReleaseHealthBody: pickAdminDashboardLocaleText(locale, { en: "There is no release health data right now.", ko: "현재 릴리스 상태 데이터가 없습니다." }),
  };

  if (state.status === "loading" || state.status === "idle") {
    return (
      <section style={cardStyle}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
        <p style={{ margin: "10px 0 0", color: "#475569" }}>{copy.loading}</p>
      </section>
    );
  }

  if (state.status === "permission_denied") {
    return (
      <AdminErrorState
        title={copy.deniedTitle}
        body={copy.deniedBody}
        guidance={{
          screen: "overview",
          slot: "shared_error_state_guidance",
        }}
        meta={buildOverviewStateMeta(locale)}
        requestId={state.error.requestId}
        tone="permission_denied"
      />
    );
  }

  if (state.status === "error") {
    return (
      <AdminErrorState
        title={copy.failedTitle}
        body={state.error.message}
        guidance={{
          screen: "overview",
          slot: "shared_error_state_guidance",
        }}
        meta={buildOverviewStateMeta(locale)}
        requestId={state.error.requestId}
        tone="error"
      />
    );
  }

  if (state.status === "empty") {
    return (
      <AdminEmptyState
        title={copy.emptyTitle}
        body={copy.emptyBody}
        guidance={{
          screen: "overview",
          slot: "shared_empty_state_guidance",
        }}
        meta={buildOverviewStateMeta(locale)}
      />
    );
  }

  const viewModel = state.viewModel;

  return (
    <section
      style={{
        display: "grid",
        gap: "18px",
      }}
    >
      <header
        style={{
          ...cardStyle,
          background:
            "linear-gradient(135deg, rgba(255, 251, 235, 0.96) 0%, rgba(255, 255, 255, 0.86) 62%, rgba(240, 249, 255, 0.78) 100%)",
          display: "grid",
          gap: "18px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "grid", gap: "10px", maxWidth: "44rem" }}>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
            <h3 style={{ margin: 0, fontSize: "1.8rem", letterSpacing: "-0.04em" }}>
              {copy.title}
            </h3>
            <p style={{ margin: 0, color: "#334155", fontSize: "1rem", lineHeight: 1.7 }}>
              {copy.description}
            </p>
            <AdminOperatorExplanationBlock
              screen="overview"
              slot="overview_top_summary"
              style={{ marginTop: "6px" }}
            />
          </div>
          <div style={{ display: "grid", gap: "10px", justifyItems: "end" }}>
            <AdminStatusBadge value={state.status} />
            <div
              style={{
                display: "grid",
                gap: "6px",
                justifyItems: "end",
                color: "#475569",
                fontSize: "0.88rem",
              }}
            >
              <span>{copy.range} {viewModel.timeRangeLabel}</span>
              <span>{copy.generated} {viewModel.generatedAtLabel}</span>
              <span>{copy.request} {viewModel.requestId}</span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.5fr) minmax(260px, 0.5fr)",
            gap: "16px",
          }}
        >
          <div
            style={{
              padding: "18px",
              borderRadius: "18px",
              background: "rgba(255, 255, 255, 0.76)",
              border: "1px solid rgba(24, 33, 43, 0.08)",
            }}
          >
            <p
              style={{
                margin: 0,
                color: "#64748b",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {copy.triageHint}
            </p>
            <p style={{ margin: "10px 0 0", fontSize: "1rem", lineHeight: 1.7, color: "#18212b" }}>
              {viewModel.primaryTriageHint}
            </p>
          </div>

          <div
            style={{
              padding: "18px",
              borderRadius: "18px",
              background: "rgba(15, 23, 42, 0.04)",
              border: "1px solid rgba(24, 33, 43, 0.08)",
              display: "grid",
              gap: "10px",
            }}
          >
            <div>
              <p
                style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
              }}
            >
                {copy.currentHotspot}
              </p>
              <p style={{ margin: "8px 0 0", fontSize: "1.2rem", fontWeight: 700 }}>
                {viewModel.currentHotspotHeadline.value}
              </p>
            </div>
            <p style={{ margin: 0, color: "#475569", fontSize: "0.9rem", lineHeight: 1.6 }}>
              {viewModel.currentHotspotHeadline.detail}
            </p>
            {viewModel.currentHotspotHeadline.drilldownTo ? (
              <AdminLocalizedLink to={viewModel.currentHotspotHeadline.drilldownTo} style={{ fontWeight: 700 }}>
                {copy.openHotspot}
              </AdminLocalizedLink>
            ) : null}
          </div>
        </div>
      </header>

      {viewModel.unavailableSources.length > 0 ? (
        <AdminErrorState
          title={copy.sourceUnavailableTitle}
          body={copy.sourceUnavailableBody}
          guidance={{
            screen: "overview",
            slot: "shared_error_state_guidance",
          }}
          requestId={viewModel.requestId}
          tone="source_unavailable"
          meta={buildOverviewSourceUnavailableMeta(viewModel, locale)}
        />
      ) : null}

      <AdminDetailPanel
        title={copy.freshnessAndContext}
        sections={[
          {
            title: copy.overviewMeta,
            items: [
              {
                key: "range",
                label: copy.range,
                value: viewModel.timeRangeLabel,
              },
              {
                key: "generatedAt",
                label: copy.generated,
                value: viewModel.generatedAtLabel,
              },
              {
                key: "requestId",
                label: copy.request,
                value: viewModel.requestId,
              },
              {
                key: "freshness",
                label: copy.freshness,
                value: <AdminStatusBadge value={toFreshnessBadgeValue(viewModel.freshness)} />,
              },
            ],
          },
          {
            title: copy.sourceStatus,
            items: [
              {
                key: "sourceStatus",
                label: copy.sourceStatus,
                value:
                  viewModel.sourceStatuses.length > 0 ? (
                    <AdminSourceStatusGrid
                      items={viewModel.sourceStatuses.map((item) => ({
                        source: item.source,
                        freshness: item.freshness,
                        sourceKindLabel: item.kindLabel,
                      }))}
                      statusLabel={copy.sourceStatus}
                    />
                  ) : (
                    "-"
                  ),
              },
            ],
          },
          {
            title: copy.operatorChecklist,
            items: [
              {
                key: "axisOrder",
                label: copy.start,
                value: copy.startBody,
              },
              {
                key: "staleSources",
                label: copy.delayed,
                value: viewModel.staleSources.length > 0 ? viewModel.staleSources.join(", ") : copy.none,
              },
              {
                key: "unavailableSources",
                label: copy.unavailable,
                value: viewModel.unavailableSources.length > 0 ? viewModel.unavailableSources.join(", ") : copy.none,
              },
            ],
          },
        ]}
      />

      {viewModel.aiRuntime ? (
        <AdminAiRuntimeSummaryStrip
          ariaLabel={viewModel.aiRuntime.title}
          items={viewModel.aiRuntime.items}
          sourceGapLabel={viewModel.aiRuntime.sourceGapLabel}
          title={viewModel.aiRuntime.title}
        />
      ) : null}

      {viewModel.observability ? (
        <AdminAggregateSummaryStrip
          ariaLabel={viewModel.observability.title}
          items={viewModel.observability.items}
          sourceGapLabel={viewModel.observability.sourceGapLabel}
          title={viewModel.observability.title}
        />
      ) : null}

      <section style={{ display: "grid", gap: "12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.primaryAxes}</p>
            <h4 style={{ margin: "8px 0 0", fontSize: "1.2rem", letterSpacing: "-0.03em" }}>
              {copy.primaryAxesTitle}
            </h4>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "16px",
          }}
        >
          {viewModel.axisEntries.map((entry) => (
            <AdminLocalizedLink
              key={entry.to}
              to={entry.to}
              style={{
                ...cardStyle,
                display: "grid",
                gap: "12px",
                color: "#18212b",
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    color: "#9a3412",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  {entry.eyebrow}
                </p>
                <h5 style={{ margin: "8px 0 0", fontSize: "1.15rem", letterSpacing: "-0.02em" }}>
                  {entry.title}
                </h5>
              </div>
              <p style={{ margin: 0, color: "#334155", fontSize: "0.94rem", lineHeight: 1.6 }}>
                {entry.description}
              </p>
              <div>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.78rem", fontWeight: 700 }}>
                  {entry.metricLabel}
                </p>
                <p style={{ margin: "8px 0 0", fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.03em" }}>
                  {entry.metricValue}
                </p>
              </div>
              <p style={{ margin: 0, color: "#475569", fontSize: "0.88rem", lineHeight: 1.6 }}>
                {entry.hint}
              </p>
            </AdminLocalizedLink>
          ))}
        </div>
      </section>

      <section style={{ display: "grid", gap: "12px" }}>
        <div>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.todayHealth}</p>
          <h4 style={{ margin: "8px 0 0", fontSize: "1.2rem", letterSpacing: "-0.03em" }}>
            {copy.todayHealthTitle}
          </h4>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "16px",
          }}
        >
          {renderMetricCard(copy.requests, viewModel.requestCountLabel, copy.requestsBody)}
          {renderMetricCard("5xx", viewModel.serverErrorCountLabel, copy.server5xxBody)}
          {renderMetricCard("4xx", viewModel.clientErrorCountLabel, copy.client4xxBody)}
          {renderMetricCard(
            viewModel.topErrorHeadline.label,
            viewModel.topErrorHeadline.value,
            viewModel.topErrorHeadline.detail,
          )}
          {renderMetricCard(
            viewModel.weakestUseCaseHeadline.label,
            viewModel.weakestUseCaseHeadline.value,
            viewModel.weakestUseCaseHeadline.detail,
          )}
        </div>
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.15fr) minmax(320px, 0.85fr)",
          gap: "16px",
        }}
      >
        <section style={{ display: "grid", gap: "16px" }}>
          <section style={{ display: "grid", gap: "8px" }}>
            <div style={{ display: "grid", gap: "6px" }}>
              <p
                style={{
                  margin: 0,
                  color: "#64748b",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {copy.diagnosticEvidence}
              </p>
              <p style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#18212b" }}>
                {copy.diagnosticEvidenceTitle}
              </p>
              <p style={{ margin: 0, color: "#475569", lineHeight: 1.6 }}>
                {copy.diagnosticEvidenceBody}
              </p>
            </div>
            <AdminOperatorExplanationBlock
              screen="overview"
              slot="overview_table_reading_order"
            />
          </section>

          <AdminDataTable
            columns={[
              {
                id: "errorCode",
                label: copy.topError,
                renderCell: (row) => (
                  <div>
                    <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                      {row.errorCode}
                    </AdminLocalizedLink>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.contextLabel}
                    </div>
                  </div>
                ),
              },
              {
                id: "count",
                label: copy.count,
                align: "right",
                renderCell: (row) => row.countLabel,
              },
              {
                id: "feature",
                label: copy.feature,
                renderCell: (row) => row.topFeature ?? "-",
              },
              {
                id: "useCase",
                label: copy.useCase,
                renderCell: (row) => row.topUseCase ?? "-",
              },
            ]}
            emptyState={<AdminEmptyState title={copy.noTopErrors} body={copy.noTopErrorsBody} />}
            getRowId={(row) => row.errorCode}
            rows={viewModel.topErrorCodes}
          />

          <AdminDataTable
            columns={[
              {
                id: "useCase",
                label: copy.weakUseCases,
                renderCell: (row) => (
                  <div>
                    <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                      {row.useCase}
                    </AdminLocalizedLink>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.detailLabel}
                    </div>
                  </div>
                ),
              },
              {
                id: "feature",
                label: copy.feature,
                renderCell: (row) => row.feature,
              },
              {
                id: "failure",
                label: copy.failure,
                align: "right",
                renderCell: (row) => row.failureCountLabel,
              },
              {
                id: "successRate",
                label: copy.rate,
                align: "right",
                renderCell: (row) => row.successRateLabel,
              },
            ]}
            emptyState={<AdminEmptyState title={copy.noUseCaseHealth} body={copy.noUseCaseHealthBody} />}
            getRowId={(row) => `${row.feature}:${row.useCase}`}
            rows={viewModel.useCaseHealth}
          />
        </section>

      </div>

      <section style={{ display: "grid", gap: "16px" }}>
        <div>
          <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.releaseAndJobs}</p>
          <h4 style={{ margin: "8px 0 0", fontSize: "1.2rem", letterSpacing: "-0.03em" }}>
            {copy.releaseAndJobsTitle}
          </h4>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
          }}
        >
          {viewModel.accountDeletionSeverityHeadline
            ? renderHotspotCard(
                viewModel.accountDeletionSeverityHeadline.label,
                viewModel.accountDeletionSeverityHeadline.value,
                viewModel.accountDeletionSeverityHeadline.detail,
                viewModel.accountDeletionSeverityHeadline.drilldownTo,
              )
            : null}
          {renderHotspotCard(
            viewModel.jobHotspotHeadline.label,
            viewModel.jobHotspotHeadline.value,
            viewModel.jobHotspotHeadline.detail,
            viewModel.jobHotspotHeadline.drilldownTo,
          )}
          {renderHotspotCard(
            viewModel.releaseHotspotHeadline.label,
            viewModel.releaseHotspotHeadline.value,
            viewModel.releaseHotspotHeadline.detail,
            viewModel.releaseHotspotHeadline.drilldownTo,
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "16px",
          }}
        >
          <AdminDataTable
            columns={[
              {
                id: "jobType",
                label: copy.jobHotspot,
                renderCell: (row) => (
                  <div>
                    <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                      {row.headline}
                    </AdminLocalizedLink>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.priorityLabel}
                    </div>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.detailLabel}
                    </div>
                  </div>
                ),
              },
              {
                id: "queued",
                label: copy.queued,
                align: "right",
                renderCell: (row) => row.queuedCountLabel,
              },
              {
                id: "failed",
                label: copy.failed,
                align: "right",
                renderCell: (row) => row.failedCountLabel,
              },
              {
                id: "succeeded",
                label: copy.succeeded,
                align: "right",
                renderCell: (row) => row.succeededCountLabel,
              },
            ]}
            emptyState={<AdminEmptyState title={copy.noJobHealth} body={copy.noJobHealthBody} />}
            getRowId={(row) => row.jobType}
            rows={viewModel.jobHealth}
          />

          <AdminDataTable
            columns={[
              {
                id: "release",
                label: copy.releaseHotspot,
                renderCell: (row) => (
                  <div>
                    <AdminLocalizedLink to={row.drilldownTo} style={{ fontWeight: 700 }}>
                      {row.release}
                    </AdminLocalizedLink>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.appVersionLabel}
                    </div>
                  </div>
                ),
              },
              {
                id: "requestCount",
                label: copy.requests,
                align: "right",
                renderCell: (row) => row.requestCountLabel,
              },
              {
                id: "failureCount",
                label: copy.failures,
                align: "right",
                renderCell: (row) => row.failureCountLabel,
              },
              {
                id: "topErrorCode",
                label: copy.topError,
                renderCell: (row) => (
                  <div>
                    <span>{row.topErrorCode ?? "-"}</span>
                    <div style={{ color: "#64748b", marginTop: "4px", fontSize: "0.84rem" }}>
                      {row.detailLabel}
                    </div>
                  </div>
                ),
              },
            ]}
            emptyState={<AdminEmptyState title={copy.noReleaseHealth} body={copy.noReleaseHealthBody} />}
            getRowId={(row) => `${row.release}:${row.appVersion ?? "none"}`}
            rows={viewModel.releaseHealth}
          />
        </div>
      </section>
    </section>
  );
}
